/**
 * @voltxampere/web — Connection animation store (Sprint 58b)
 *
 * VISION §6.E P1 karakter polish closure — 4th of 5 effects.
 *
 * Two animation modes:
 *
 *   - **drawing**   — WireTool is actively routing a draft wire.
 *                     Marching-ants stroke-dash animation plays as
 *                     long as the wire stays in the drafting state.
 *   - **committed** — A new wire has just landed in `currentCircuit`.
 *                     A 600 ms flash highlight runs once then the
 *                     entry is auto-evicted.
 *
 * `prefersReducedMotion` (Sprint 29-5 store/fx-state.ts) gates the
 * animation phase update. Canvas renderer keeps the static stroke
 * when reduced motion is true.
 *
 * Copyright © 2026 360 Studio
 * Proprietary — All rights reserved.
 */

import { computed, effect, signal, type ReadonlySignal } from "@preact/signals-core";
import type { Point, Wire } from "@voltxampere/schema";

import { currentCircuit } from "./circuit.js";

/** Lifecycle TTL for the post-commit flash overlay (ms). */
export const FLASH_TTL_MS = 600;

export interface ConnectionAnimationState {
  readonly mode: "drawing" | "committed";
  /** Wire identifier — `null` while drafting (no id yet). */
  readonly wireId: string | null;
  /** Wire path snapshot (world coords) for the renderer. */
  readonly path: readonly Point[];
  /** `performance.now()` at activation; flash entries age out at FLASH_TTL_MS. */
  readonly startedAtMs: number;
}

const drawingStateInternal = signal<ConnectionAnimationState | null>(null);
const committedFlashesInternal = signal<readonly ConnectionAnimationState[]>([]);

/**
 * Combined animation snapshot consumed by the renderer. Returns
 * drawing + every active committed-flash entry. Renderer is free
 * to read this each rAF tick — array allocations are cheap on
 * the wire-count scale we deal with.
 */
export const connectionAnimationStates: ReadonlySignal<readonly ConnectionAnimationState[]> =
  computed(() => {
    const out: ConnectionAnimationState[] = [];
    if (drawingStateInternal.value !== null) out.push(drawingStateInternal.value);
    for (const f of committedFlashesInternal.value) out.push(f);
    return out;
  });

/**
 * Has-active-animation derived signal for the rAF reschedule guard
 * inside `<vxa-canvas>`. Cheap O(1) read.
 */
export const hasActiveConnectionAnimation: ReadonlySignal<boolean> = computed(
  () => drawingStateInternal.value !== null || committedFlashesInternal.value.length > 0,
);

export function setDrawingAnimation(state: ConnectionAnimationState | null): void {
  drawingStateInternal.value = state;
}

export function pushCommittedFlash(wire: Wire): void {
  const entry: ConnectionAnimationState = {
    mode: "committed",
    wireId: wire.id,
    path: wire.path,
    startedAtMs: performance.now(),
  };
  committedFlashesInternal.value = [...committedFlashesInternal.value, entry];
}

/**
 * Prune flash entries older than {@link FLASH_TTL_MS}. The canvas
 * fx loop calls this once per frame; cheap O(n) on small arrays.
 */
export function pruneExpiredFlashes(nowMs: number): void {
  const cutoff = nowMs - FLASH_TTL_MS;
  const current = committedFlashesInternal.value;
  if (current.length === 0) return;
  const kept = current.filter((e) => e.startedAtMs > cutoff);
  if (kept.length !== current.length) {
    committedFlashesInternal.value = kept;
  }
}

/**
 * Boot — observes `currentCircuit` and fires a flash for every
 * new wire that lands. Detection uses a prevWireIds set so a
 * single-mutation (one new wire) and a multi-mutation (e.g.
 * preset load with 4 wires) both surface the same per-wire flash.
 */
let activeUnsubscribe: (() => void) | null = null;

export function bootConnectionAnimation(): () => void {
  if (activeUnsubscribe) return activeUnsubscribe;
  let prevWireIds = new Set<string>();
  let firstRun = true;
  const dispose = effect(() => {
    const circuit = currentCircuit.value;
    const currentIds = new Set<string>();
    if (circuit !== null) {
      for (const w of circuit.wires) currentIds.add(w.id);
    }
    if (firstRun) {
      firstRun = false;
      prevWireIds = currentIds;
      return;
    }
    // New wires (in currentIds but not in prevWireIds) → flash.
    if (circuit !== null) {
      for (const w of circuit.wires) {
        if (!prevWireIds.has(w.id)) pushCommittedFlash(w);
      }
    }
    prevWireIds = currentIds;
  });
  activeUnsubscribe = (): void => {
    dispose();
    activeUnsubscribe = null;
    firstRun = true;
  };
  return activeUnsubscribe;
}

/** Test helper — full reset. */
export function resetConnectionAnimationForTests(): void {
  activeUnsubscribe?.();
  activeUnsubscribe = null;
  drawingStateInternal.value = null;
  committedFlashesInternal.value = [];
}
